import { useSelector } from 'react-redux';
import { CustomizePlanButtonGroup, OnboardingChrome } from '../components';
import { selectClient } from '../model';
import { SlideContent } from '../components/SlideContent';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined'; // nursing home
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'; // independent living
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined'; // assisted living
import { FiChevronsRight } from 'react-icons/fi';
import { PageLink } from '../components/PageLink';
import { OnboardingFundingSources } from '../pages';

import {
  carePhaseDefs,
  CarePhase,
  careEnvironmentDefs,
  carePhaseDefList,
  formatCurrency,
  formatThousands,
  CareEnvironment,
  formatPercent,
  pickClientCareEnvironmentCosts,
  isHealthcareInflationRateDefault,
} from '@shared';
import { OnboardingCareSettingsPhaseThree } from '.';
import { useState } from 'react';
import CareEnvironmentCostModal from '../components/CareEnvironmentCostModal';
import EditHealthCareInflationModal from '../components/EditHealthCareInflationModal';
import { EditIconButton } from '@client/components/EditIconButton';

export function OnboardingCarePhasesSummary({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const {
    phaseCosts,
    appliedCareEnvironments,
    clientPhaseInflationFactors,
    clientYearsTillPhaseEnd,
    intakeSurvey: { clientLtcZipCode },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    clientCalculationSettings: { healthCareInflationRate },
  } = client;

  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  const [selectedCareEnvironment, setSelectedCareEnvironment] = useState<CareEnvironment | null>(
    null,
  );

  const handleEditCostClick = (careEnvironment: CareEnvironment) => {
    setSelectedCareEnvironment(careEnvironment);
    setOpenEditCostModal(true);
  };

  const handleEditInflationClick = () => {
    setOpenEditInflationModal(true);
  };

  const carePhaseData = carePhaseDefList.map(phase => {
    const singlePhaseCosts = phaseCosts[phase.index];
    const phaseLabel = carePhaseDefs[phase.value].label;
    const yearsTillPhaseEnd = clientYearsTillPhaseEnd[phase.value];
    const {
      inHomeEnvironment,
      phaseInflatedProfessionalShareCost,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
    } = singlePhaseCosts;
    const phaseInflationFactor = clientPhaseInflationFactors[phase.value];
    const careEnvironment = appliedCareEnvironments[phase.value];
    const careEnvironmentLabel = careEnvironmentDefs[careEnvironment].label;
    const isDurationCustom = singlePhaseCosts.isDurationCustom;
    const { rateAmount, hasCustomRate, rateLocaleDescription } = pickClientCareEnvironmentCosts(
      client,
      careEnvironment,
    );
    return {
      id: phase.index,
      phaseLabel,
      phaseInflatedProfessionalShareCost,
      careEnvironment,
      careEnvironmentLabel,
      rateAmount,
      hasCustomRate,
      rateLocaleDescription,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
      phaseInflationFactor,
      inHomeEnvironment,
      yearsTillPhaseEnd,
      isDurationCustom,
    };
  });

  const multSign = (
    <div className="items-left mt-1 flex flex-grow basis-[5%] flex-col text-lg font-light">x</div>
  );
  const equalSign = (
    <div className="items-left mt-1 flex flex-grow  basis-[5%] flex-col text-lg font-light">=</div>
  );

  const iconPicker = (careEnvironment: CareEnvironment) => {
    switch (careEnvironment) {
      case CareEnvironment.home:
        return (
          <HomeOutlinedIcon
            fontSize="large"
            sx={{
              paddingBottom: '2px',
              stroke: '#ffffff',
              strokeWidth: 1,
            }}
          />
        );
      case CareEnvironment.fullCareFacility:
        return (
          <DomainAddOutlinedIcon
            fontSize="large"
            sx={{
              paddingTop: '4px',
              paddingBottom: '4px',
              stroke: '#ffffff',
              strokeWidth: 1,
            }}
          />
        );
      case CareEnvironment.assistedLiving:
        return (
          <AddHomeWorkOutlinedIcon
            fontSize="large"
            sx={{
              paddingTop: '4px',
              paddingBottom: '4px',
              stroke: '#ffffff',
              strokeWidth: 1,
            }}
          />
        );
      case CareEnvironment.independentLiving:
        return (
          <HomeWorkOutlinedIcon
            fontSize="large"
            sx={{
              paddingTop: '4px',
              paddingBottom: '4px',
              stroke: '#ffffff',
              strokeWidth: 1,
            }}
          />
        );
    }
  };

  return (
    <OnboardingChrome
      slidePage={OnboardingCarePhasesSummary}
      nextButtonLabel={`Begin Funding Plan`}
      previousSlidePage={
        appliedCareEnvironments[CarePhase.fullCare] === CareEnvironment.home
          ? undefined // default to previous slide
          : OnboardingCareSettingsPhaseThree // skip to care settings
      }
    >
      <SlideContent headerText={<span data-testid="care-summary-header">Cost Total</span>}>
        <CustomizePlanButtonGroup
          client={client}
          className="my-5 lg:mb-10"
        />
        <dl className="space-y-6 divide-y divide-gray-900/10 md:mt-14">
          {carePhaseData.map(phase => {
            if (phase.yearsTillPhaseEnd) {
              return (
                <Disclosure
                  as="div"
                  key={phase.id}
                  className="pt-6"
                >
                  {({ open }) => (
                    <>
                      <dt>
                        <Disclosure.Button
                          className="flex w-full items-start justify-between text-left text-gray-900"
                          data-testid={`care-summary-phase-${phase.phaseLabel.replace(/\s+/g, '-').toLowerCase()}-button`}
                        >
                          <div className="flex w-full gap-5 md:grid md:grid-cols-8 md:grid-rows-1 md:gap-5">
                            <div
                              id="mobile version"
                              className="md:hidden"
                            >
                              <div className="col-span-1 row-span-1 basis-6 text-base font-semibold md:col-span-2 md:text-2xl">
                                {phase.phaseLabel}
                              </div>
                              <div className="basis-6 gap-1 text-sm font-light md:col-span-3 md:flex md:text-2xl">
                                <span className="hidden md:flex">
                                  {iconPicker(phase.careEnvironment)}
                                </span>

                                {phase.careEnvironmentLabel}
                              </div>
                            </div>

                            {/* only shown for md lg screens. kind of hacky */}
                            <div className="hidden text-base font-semibold md:col-span-2 md:row-span-1 md:flex md:basis-6 md:text-2xl">
                              {phase.phaseLabel}
                            </div>
                            <div className="hidden basis-6 gap-1 text-sm font-light md:col-span-3 md:flex md:text-2xl">
                              <span className="hidden md:flex">
                                {iconPicker(phase.careEnvironment)}
                              </span>
                              {phase.careEnvironmentLabel}
                            </div>
                            {/* only shown for md lg screens*/}

                            <div className="flex-1 text-end text-base font-light md:col-span-3 md:justify-self-end md:text-2xl">
                              {formatCurrency(phase.phaseInflatedProfessionalShareCost)}
                            </div>
                          </div>
                          <span className="ml-6 flex h-7 items-center">
                            {open ? (
                              <ChevronUpIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            ) : (
                              <ChevronDownIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel
                        as="dd"
                        className="py-10 pr-12"
                      >
                        <div className="-translate-y-5 border-t border-gray-200"></div>
                        <div className="ml-4 flex w-full flex-col gap-2 md:flex-row">
                          <div className="flex basis-[20%] flex-col">
                            <div className="flex items-center gap-1 text-sm font-light md:text-xl">
                              {`${formatCurrency(phase.rateAmount)}/${phase.inHomeEnvironment ? 'hr' : 'mo'}`}
                              <EditIconButton
                                onClick={() => handleEditCostClick(phase.careEnvironment)}
                                tooltipText="Edit cost"
                              />
                            </div>
                            <div className="text-sm font-semibold">
                              {phase.rateLocaleDescription}
                            </div>
                          </div>
                          {multSign}
                          <div className="flex basis-[20%] flex-col">
                            <div className="text-sm font-light md:text-xl">
                              {phase.inHomeEnvironment
                                ? `${formatThousands(phase.phaseProfessionalCareHoursProvided)} hours`
                                : `${formatThousands(phase.phaseCareMonthsNeeded)} months`}
                            </div>
                            <div className="text-sm font-semibold">
                              {phase.inHomeEnvironment
                                ? `Professional care hours`
                                : `${phase.isDurationCustom ? 'Custom duration' : 'Estimated duration'}`}
                            </div>
                          </div>
                          {multSign}
                          <div className="flex basis-[20%] flex-col">
                            <div className="flex items-center gap-1 text-sm font-light md:text-xl">
                              {formatPercent(phase.phaseInflationFactor, 1)}
                              <EditIconButton
                                onClick={handleEditInflationClick}
                                tooltipText="Edit inflation rate"
                              />
                            </div>
                            <div className="text-sm font-semibold">
                              Cost growth over{' '}
                              {phase.yearsTillPhaseEnd
                                ? formatThousands(phase.yearsTillPhaseEnd)
                                : '0'}{' '}
                              years
                              {isHealthcareInflationRateDefault(healthCareInflationRate)
                                ? ''
                                : ' (custom rate)'}
                            </div>
                          </div>
                          {equalSign}
                          <div className="flex basis-[20%] flex-col">
                            <div className="text-sm font-light md:text-xl">
                              {formatCurrency(phase.phaseInflatedProfessionalShareCost)}
                            </div>
                            <div className="text-sm font-semibold md:text-base">Total</div>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              );
            } else {
              return null;
            }
          })}
        </dl>
        <div className="mt-6 grid w-full grid-cols-8 gap-5 border-t-4 border-gray-900/10 pb-8 pt-6 md:pb-4">
          <div className="col-span-2 text-lg font-semibold md:text-2xl">Total</div>
          <div
            className="col-span-3 col-start-6 mr-12 justify-self-end text-lg font-light md:text-2xl"
            data-testid="care-summary-total-amount"
          >
            {formatCurrency(allPhaseInflatedProfessionalShareCost)}
          </div>
        </div>
        <PageLink
          className="mt-6 hidden gap-2 text-lg font-light hover:underline md:flex md:text-2xl"
          to={OnboardingFundingSources}
          targetProps={{ clientId }}
        >
          <div className="flex items-center gap-1 font-normal">
            <FiChevronsRight className="h-6 w-6" />
            Next up:
          </div>
          <span> Let&apos;s explore strategies to finance these costs.</span>
        </PageLink>
        {selectedCareEnvironment && (
          <CareEnvironmentCostModal
            selectedCareEnvironment={selectedCareEnvironment}
            open={openEditCostModal}
            setOpen={setOpenEditCostModal}
          />
        )}
        <EditHealthCareInflationModal
          open={openEditInflationModal}
          setOpen={setOpenEditInflationModal}
        />
      </SlideContent>
    </OnboardingChrome>
  );
}
